'use client';

import { useEffect, useRef, useState } from 'react';

import { encodeBmp, verifyBmpHeader } from '@/lib/browser-bmp-encoder';

export default function BrowserJpgToBmp() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('image');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    setError(null);
    setInfo(null);
    if (!file) return;
    if (typeof window === 'undefined') return;
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'image');
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError('That file could not be read as an image. Try another JPG.');
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    if (typeof document === 'undefined') return;
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Your browser blocked the canvas needed to convert this image.');
      }
      ctx.drawImage(image, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = encodeBmp(imageData.data, canvas.width, canvas.height, 24);
      const problems = verifyBmpHeader(result.bytes, canvas.width, canvas.height, 24);
      if (problems.length > 0) {
        setError(`BMP header check failed: ${problems[0]}`);
        return;
      }
      const blob = new Blob([result.bytes], { type: 'image/bmp' });
      setDownloadUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setInfo(
        `Verified 24-bit BMP, ${canvas.width}x${canvas.height}px, ${(result.fileSize / 1024).toFixed(0)} KB.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not encode the BMP file.');
    }
  }, [image]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">BMP</span>
        <h2 className="tool-live-title">Convert JPG to BMP</h2>
        <p className="tool-live-subtitle">
          Upload a JPG or JPEG and download an uncompressed 24-bit BMP. Everything runs in your
          browser and nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose a JPG image</span>
            <input
              accept="image/jpeg,.jpg,.jpeg"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}.bmp`}
                href={downloadUrl}
              >
                Download BMP
              </a>
            ) : null}
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {info && !error ? <p className="tool-live-panel-note">{info}</p> : null}
          {image || error ? null : <p className="tool-live-panel-note">Pick a JPG to convert.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            The BMP is built from the canvas pixels with a self-contained encoder. Your image never
            leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
